import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="h5"
      sx={{
        color: 'primary.main',
        fontWeight: 600,
        mb: 1.5,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const SubSection = ({ title, children }) => (
  <Box sx={{ mt: 2.5, mb: 1.5 }}>
    <Typography
      variant="h6"
      sx={{ color: 'secondary.dark', fontWeight: 600, mb: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList = ({ items }) => (
  <List dense disablePadding sx={{ pl: 2 }}>
    {items.map((item, i) => (
      <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
        <ListItemText
          primary={item}
          primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.8 } }}
        />
      </ListItem>
    ))}
  </List>
);

const HighlightBox = ({ children }) => (
  <Alert
    severity="info"
    icon={false}
    sx={{
      my: 2,
      borderLeft: '4px solid',
      borderLeftColor: 'secondary.main',
      backgroundColor: 'grey.50',
    }}
  >
    {children}
  </Alert>
);

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5, pb: 3, borderBottom: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Last Updated: January 13, 2026
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Effective for:</strong> Smart Industries Website & Mobile App
          </Typography>
        </Box>

        {/* Section 1 */}
        <Section number={1} title="Introduction">
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Welcome to <strong>Smart Industries</strong>, operated by <strong>Smart Industries LLC</strong>.
            We are committed to protecting your privacy and ensuring transparency in how we handle your data.
          </Typography>

          <HighlightBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon color="primary" />
              <Typography variant="body2">
                <strong>Privacy-First Principle:</strong> Your Personal Identifiable Information (PII) is NEVER
                stored permanently. All breach searches are processed in memory only and immediately discarded.
              </Typography>
            </Box>
          </HighlightBox>

          <Typography variant="body1" paragraph>This Privacy Policy applies to:</Typography>
          <BulletList items={[
            <><Link href="https://smartindustriesllc.com" target="_blank" rel="noopener">smartindustriesllc.com</Link> (Website)</>,
            'Mobile App: Smart Industries (Android)',
            'Backend API services',
          ]} />
        </Section>

        {/* Section 2 */}
        <Section number={2} title="Data We Collect">
          <SubSection title="2.1 Search Identifiers (NOT STORED)">
            <Typography variant="body1" paragraph>When you check for data breaches, you may enter:</Typography>
            <BulletList items={[
              <><strong>Email addresses</strong></>,
              <><strong>Usernames</strong> (premium feature)</>,
              <><strong>Phone numbers</strong> (premium feature)</>,
            ]} />
            <Typography variant="body1" paragraph>
              <strong>Important:</strong> These identifiers are sent to our backend API and third-party breach
              databases (Have I Been Pwned, XposedOrNot) but are <strong>NEVER stored permanently</strong>. They
              exist only in memory during the API request and are immediately discarded.
            </Typography>
          </SubSection>

          <SubSection title="2.2 Identity Recovery Data (Optional)">
            <Typography variant="body1" paragraph>If you use the Identity Recovery feature in our mobile app:</Typography>
            <BulletList items={[
              <><strong>Email address</strong> (optional, for progress sync)</>,
              <><strong>Recovery checklist progress</strong> (which agencies you've contacted)</>,
              <><strong>Stored:</strong> Encrypted on your device using hardware-backed encryption</>,
              <><strong>Deletion:</strong> You can delete all recovery data anytime via Settings</>,
            ]} />
          </SubSection>

          <SubSection title="2.3 Device & Technical Information">
            <BulletList items={[
              'Device type (iOS, Android, Desktop)',
              'Operating system version',
              'App version number',
              'Browser type and version (web only)',
              'IP address (for security and fraud prevention)',
            ]} />
          </SubSection>

          <SubSection title="2.4 Usage Analytics (Anonymous)">
            <BulletList items={[
              'Pages visited',
              'Features used',
              'App performance metrics',
              'Crash reports',
            ]} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Note:</strong> Analytics are anonymized and aggregated. No personally identifiable information is included.
            </Typography>
          </SubSection>

          <SubSection title="2.5 Advertising Data">
            <Typography variant="body1" paragraph>
              We display advertisements through <strong>Google AdSense</strong> (website) and{' '}
              <strong>Google AdMob</strong> (mobile app). These services may collect:
            </Typography>
            <BulletList items={[
              'Cookies and similar technologies to serve ads',
              'Ad interaction data (clicks, impressions, viewability)',
              'Device information (browser type, operating system, screen size)',
              'IP address (for geographic ad targeting and fraud prevention)',
              'Device advertising ID (can be reset in device settings)',
              'Browsing behavior on our site and other sites (if personalized ads are enabled)',
            ]} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Note:</strong> By default, we request non-personalized ads. However, Google may still use
              limited data for ad delivery and measurement.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 3 */}
        <Section number={3} title="How We Use Your Data">
          <SubSection title="3.1 Breach Checking">
            <BulletList items={[
              'Query Have I Been Pwned and XposedOrNot databases',
              'Display breach results to you',
              'Provide related class-action lawsuit information',
            ]} />
          </SubSection>
          <SubSection title="3.2 Identity Recovery">
            <BulletList items={[
              'Save your recovery progress (encrypted on device)',
              'Sync progress across devices (if you provide email)',
              'Send recovery summary via email (optional)',
            ]} />
          </SubSection>
          <SubSection title="3.3 Service Improvement">
            <BulletList items={[
              'Analyze usage patterns (anonymized)',
              'Fix bugs and improve performance',
              'Develop new features',
            ]} />
          </SubSection>
          <SubSection title="3.4 Security & Fraud Prevention">
            <BulletList items={[
              'Detect and prevent abuse',
              'Enforce rate limits',
              'Protect against DDoS attacks',
            ]} />
          </SubSection>
          <SubSection title="3.5 Legal Compliance">
            <BulletList items={[
              'Comply with legal obligations',
              'Respond to law enforcement requests (only when legally required)',
            ]} />
          </SubSection>
        </Section>

        {/* Section 4 */}
        <Section number={4} title="Data Sharing">
          <HighlightBox>
            <Typography variant="body2"><strong>We do NOT sell your personal information to anyone.</strong></Typography>
          </HighlightBox>
          <Typography variant="body1" paragraph>We may share data with:</Typography>

          <SubSection title="4.1 Third-Party Services">
            <BulletList items={[
              <><strong>Have I Been Pwned API</strong> - Breach checking (they don't store queries)</>,
              <><strong>XposedOrNot API</strong> - Free breach database (they don't store queries)</>,
              <><strong>Google AdSense</strong> - Display advertising on website (<Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">Google Privacy Policy</Link>)</>,
              <><strong>Google AdMob</strong> - Display advertising in mobile app (<Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">Google Privacy Policy</Link>)</>,
              <><strong>Cloud Hosting Provider</strong> - Backend hosting (HTTPS encrypted, SOC 2 compliant)</>,
              <><strong>OpenAI API</strong> - News article summarization (no PII sent)</>,
            ]} />
          </SubSection>
          <SubSection title="4.2 Legal Authorities">
            <Typography variant="body1" paragraph>
              We may disclose data if required by law, court order, or government request.
            </Typography>
          </SubSection>
          <SubSection title="4.3 Business Transfers">
            <Typography variant="body1" paragraph>
              If Smart Industries LLC is acquired or merged, your data may be transferred to the new entity.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 5 */}
        <Section number={5} title="Data Retention">
          <SubSection title="5.1 Search Queries">
            <Typography variant="body1" paragraph>
              <strong>NOT STORED</strong> - Processed in memory only and immediately discarded.
            </Typography>
          </SubSection>
          <SubSection title="5.2 Identity Recovery Data">
            <Typography variant="body1" paragraph>
              <strong>Stored until you delete it</strong> - You have full control. Delete via Settings &rarr; Delete All Data.
            </Typography>
          </SubSection>
          <SubSection title="5.3 Analytics Data">
            <Typography variant="body1" paragraph>
              <strong>Up to 24 months</strong> - Anonymous aggregate data for service improvement.
            </Typography>
          </SubSection>
          <SubSection title="5.4 Log Files">
            <Typography variant="body1" paragraph>
              <strong>30 days</strong> - Server logs for security and debugging (IP addresses anonymized after 7 days).
            </Typography>
          </SubSection>
        </Section>

        {/* Section 6 */}
        <Section number={6} title="Your Rights">
          <SubSection title="6.1 For EU Users (GDPR)">
            <BulletList items={[
              <><strong>Right to Access:</strong> Request a copy of your data</>,
              <><strong>Right to Rectification:</strong> Correct inaccurate data</>,
              <><strong>Right to Erasure ("Right to be Forgotten"):</strong> Delete your data</>,
              <><strong>Right to Data Portability:</strong> Export your data in machine-readable format</>,
              <><strong>Right to Object:</strong> Opt out of certain types of processing</>,
              <><strong>Right to Restrict Processing:</strong> Limit how we use your data</>,
            ]} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Contact us at{' '}
              <Link href="mailto:mikel.smart@smartindustriesllc.com">mikel.smart@smartindustriesllc.com</Link>{' '}
              to exercise these rights.
            </Typography>
          </SubSection>

          <SubSection title="6.2 For California Users (CCPA/CPRA)">
            <BulletList items={[
              <><strong>Right to Know:</strong> Request disclosure of data collected in the past 12 months</>,
              <><strong>Right to Delete:</strong> Request deletion of personal information</>,
              <><strong>Right to Opt-Out:</strong> We don't sell your data, so no opt-out needed</>,
              <><strong>Right to Non-Discrimination:</strong> We won't discriminate for exercising your rights</>,
            ]} />
          </SubSection>

          <SubSection title="6.3 For All Users">
            <Typography variant="body1" paragraph>You can:</Typography>
            <BulletList items={[
              <><strong>Delete recovery data:</strong> Use "Delete All Data" in mobile app Settings</>,
              <><strong>Opt out of analytics:</strong> Enable "Do Not Track" in your browser</>,
              <><strong>Reset advertising ID:</strong> Device Settings &rarr; Privacy &rarr; Advertising</>,
              <><strong>Request data deletion:</strong> Visit our <Link component={RouterLink} to="/data-deletion">data deletion page</Link> or email us at <Link href="mailto:mikel.smart@smartindustriesllc.com">mikel.smart@smartindustriesllc.com</Link></>,
            ]} />
          </SubSection>
        </Section>

        {/* Section 7 */}
        <Section number={7} title="Data Security">
          <Typography variant="body1" paragraph>We use industry-standard security measures:</Typography>

          <SubSection title="7.1 Encryption">
            <BulletList items={[
              <><strong>In Transit:</strong> HTTPS/TLS 1.3 for all API communications</>,
              <><strong>At Rest (Mobile):</strong> Hardware-backed encryption (Android: EncryptedSharedPreferences with StrongBox/TEE)</>,
            ]} />
          </SubSection>
          <SubSection title="7.2 Infrastructure">
            <BulletList items={[
              'Secure cloud hosting (SOC 2 compliant infrastructure)',
              'Regular security audits',
              'Automated vulnerability scanning',
              'Firewall protection and DDoS mitigation',
            ]} />
          </SubSection>
          <SubSection title="7.3 Access Controls">
            <BulletList items={[
              'Principle of least privilege',
              'Multi-factor authentication for admin access',
              'Encrypted database credentials',
            ]} />
          </SubSection>

          <HighlightBox>
            <Typography variant="body2">
              <strong>Note:</strong> No system is 100% secure. While we implement best practices, we cannot guarantee absolute security.
            </Typography>
          </HighlightBox>
        </Section>

        {/* Section 8 */}
        <Section number={8} title="Children's Privacy">
          <Typography variant="body1" paragraph>
            This service is <strong>not intended for users under 13 years old</strong> (or 16 in the EU).
          </Typography>
          <Typography variant="body1" paragraph>
            We do not knowingly collect personal information from children. If you are a parent or guardian and
            believe your child has provided us with personal information, please contact us immediately at{' '}
            <Link href="mailto:mikel.smart@smartindustriesllc.com">mikel.smart@smartindustriesllc.com</Link>,
            and we will delete it.
          </Typography>
        </Section>

        {/* Section 9 */}
        <Section number={9} title="International Data Transfers">
          <Typography variant="body1" paragraph>
            Our services are operated from the <strong>United States</strong>. If you access our services from
            outside the US, your data may be transferred to, stored, and processed in the US.
          </Typography>
          <Typography variant="body1" paragraph>
            For EU users: We comply with GDPR requirements for international data transfers using Standard
            Contractual Clauses (SCCs).
          </Typography>
        </Section>

        {/* Section 10 */}
        <Section number={10} title="Cookies & Tracking">
          <SubSection title="10.1 Website">
            <Typography variant="body1" paragraph>We use the following cookies:</Typography>
            <BulletList items={[
              <><strong>Essential cookies:</strong> Login sessions (admin only)</>,
              <><strong>Advertising cookies:</strong> Google AdSense uses cookies to serve ads and measure performance</>,
            ]} />
          </SubSection>
          <SubSection title="10.2 Managing Ad Cookies">
            <Typography variant="body1" paragraph>You can control advertising cookies through:</Typography>
            <BulletList items={[
              <><Link href="https://adssettings.google.com/" target="_blank" rel="noopener">Google Ads Settings</Link> - Opt out of personalized ads</>,
              <><Link href="https://optout.aboutads.info/" target="_blank" rel="noopener">Digital Advertising Alliance</Link> - Opt out of interest-based ads</>,
              <><Link href="https://optout.networkadvertising.org/" target="_blank" rel="noopener">Network Advertising Initiative</Link> - Consumer opt-out</>,
              'Your browser settings - Block third-party cookies',
            ]} />
          </SubSection>
          <SubSection title="10.3 Mobile App">
            <BulletList items={[
              'No cookies (native app)',
              'Local storage for recovery progress only (encrypted)',
              'AdMob may use device advertising ID (non-personalized ads by default)',
              'Reset advertising ID: Device Settings \u2192 Privacy \u2192 Advertising',
            ]} />
          </SubSection>
          <SubSection title="10.4 Do Not Track">
            <Typography variant="body1" paragraph>
              We respect "Do Not Track" browser signals where technically feasible. Note that Google AdSense
              may not fully honor DNT signals.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 11 */}
        <Section number={11} title="Changes to Privacy Policy">
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. Changes will be posted on this page with an
            updated "Last Updated" date.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Material changes:</strong> If we make significant changes that affect your rights, we will notify you via:
          </Typography>
          <BulletList items={[
            'In-app notification (mobile app)',
            'Email (if you provided one)',
            'Prominent notice on our website',
          ]} />
          <Typography variant="body1" paragraph>
            Continued use of our services after changes constitutes acceptance of the updated Privacy Policy.
          </Typography>
        </Section>

        {/* Section 12 */}
        <Section number={12} title="Legal Basis for Processing (GDPR)">
          <Typography variant="body1" paragraph>We process your personal data based on:</Typography>
          <BulletList items={[
            <><strong>Consent:</strong> When you voluntarily provide information (breach checking, identity recovery)</>,
            <><strong>Legitimate Interest:</strong> Service improvement, security, fraud prevention</>,
            <><strong>Legal Obligation:</strong> Compliance with laws and regulations</>,
          ]} />
          <Typography variant="body1" paragraph>
            You can withdraw consent at any time by contacting us or using in-app deletion features.
          </Typography>
        </Section>

        {/* Section 13 */}
        <Section number={13} title="Data Breach Notification">
          <Typography variant="body1" paragraph>
            In the unlikely event of a data breach affecting your personal information, we will:
          </Typography>
          <BulletList items={[
            <><strong>Notify affected individuals</strong> without undue delay via email (if available) or prominent website notice</>,
            <><strong>Notify relevant supervisory authorities</strong> within 72 hours where required by GDPR</>,
            <><strong>Provide details</strong> about the nature of the breach, data affected, and recommended protective actions</>,
          ]} />
          <HighlightBox>
            <Typography variant="body2">
              <strong>Note:</strong> Due to our privacy-first architecture (ephemeral processing of breach queries),
              the risk of personal data exposure from our systems is minimal. We do not store the emails, phone
              numbers, or usernames you check for breaches.
            </Typography>
          </HighlightBox>
        </Section>

        {/* Section 14 */}
        <Section number={14} title="California Privacy Rights (CPRA Supplement)">
          <Typography variant="body1" paragraph>
            For California residents, in addition to the rights listed in Section 6.2:
          </Typography>

          <SubSection title="Categories of Personal Information Collected (Past 12 Months)">
            <BulletList items={[
              <><strong>Identifiers</strong> (email, phone, username): Processed but NOT retained</>,
              <><strong>Internet/Network Activity</strong> (device info, crash logs): Collected for analytics</>,
              <><strong>Advertising Identifiers</strong>: Collected by Google AdMob SDK</>,
            ]} />
          </SubSection>
          <SubSection title="Sensitive Personal Information">
            <Typography variant="body1" paragraph>
              We do not collect sensitive personal information as defined by CPRA.
            </Typography>
          </SubSection>
          <SubSection title="Sale or Sharing of Personal Information">
            <Typography variant="body1" paragraph>
              We do <strong>NOT</strong> "sell" or "share" your personal information as defined by California law.
            </Typography>
          </SubSection>
          <SubSection title="Right to Correct">
            <Typography variant="body1" paragraph>
              You have the right to request correction of inaccurate personal information. Contact us at{' '}
              <Link href="mailto:mikel.smart@smartindustriesllc.com">mikel.smart@smartindustriesllc.com</Link>.
            </Typography>
          </SubSection>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* Contact Box */}
        <Paper
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ color: 'secondary.main', mb: 1 }}>
            Contact Us
          </Typography>
          <Typography variant="body1"><strong>Smart Industries LLC</strong></Typography>
          <Typography variant="body2">
            Email:{' '}
            <Link href="mailto:mikel.smart@smartindustriesllc.com" sx={{ color: 'secondary.main' }}>
              mikel.smart@smartindustriesllc.com
            </Link>
          </Typography>
          <Typography variant="body2">
            Website:{' '}
            <Link href="https://smartindustriesllc.com" sx={{ color: 'secondary.main' }}>
              smartindustriesllc.com
            </Link>
          </Typography>

          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

          <Typography variant="body2" sx={{ fontWeight: 600 }}>For Data Deletion Requests:</Typography>
          <Typography variant="body2">
            Online:{' '}
            <Link component={RouterLink} to="/data-deletion" sx={{ color: 'secondary.main' }}>
              smartindustriesllc.com/data-deletion
            </Link>
          </Typography>
          <Typography variant="body2">Mobile App: Settings &rarr; Delete All Data</Typography>
          <Typography variant="body2">
            Email:{' '}
            <Link href="mailto:mikel.smart@smartindustriesllc.com" sx={{ color: 'secondary.main' }}>
              mikel.smart@smartindustriesllc.com
            </Link>
          </Typography>
          <Typography variant="body2">Response time: Within 30 days (GDPR) / 45 days (CCPA)</Typography>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <strong>Virtus Pre Nummis</strong> - Valor Before Money
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            &copy; {new Date().getFullYear()} Smart Industries LLC. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
